import React, { memo, useCallback, useRef } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { FieldArray, useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { getVendorFeesSaving } from 'src/selectors/paymentConfig'
import { SectionCard } from 'src/components/shared/SectionCard'
import { buildEmptyRow } from './VendorFeesForm.mapper'
import { VendorFeeRow } from './VendorFeeRow'
import type { VendorFeesFormValues } from './VendorFeesForm.interface'
import { GSIcon } from 'src/components/shared/GSIcon'

/**
 * Each fee is its own labelled block (`VendorFeeRow`) rather than Retool's seven-column table,
 * which needed a horizontal scroll at this page width. One deliberate departure from Retool is
 * kept: there is no permanent trailing blank row — an explicit Add button replaces it.
 */
interface Props {
  // Without `payout_settings_manage`: rows disabled, no Remove, no Add and no Save.
  isReadOnly: boolean
}

const VendorFeesFormContentComponent = ({ isReadOnly }: Props) => {
  const isSaving = useSelector(getVendorFeesSaving)
  const { values, dirty } = useFormikContext<VendorFeesFormValues>()

  // Counts rows added in this editing session. A ref rather than state: bumping it must not
  // itself trigger a render, and the ids only need to be unique within one mounted form.
  const addedRowCount = useRef(0)

  const buildNextRow = useCallback(() => {
    addedRowCount.current += 1

    return buildEmptyRow(`row-new-${addedRowCount.current}`)
  }, [])

  return (
    <FieldArray name="rows">
      {({ push, remove }) => {
        return (
          <SectionCard
            title="GigSafe Vendor Fees"
            subtitle="Extra fee lines applied to contractor payouts."
          >
            {values.rows.length > 0 ? (
              <div css={tw`flex flex-col gap-4`}>
                {values.rows.map((row, index) => {
                  return (
                    // Keyed by the row's own id, never by index: index keys make React
                    // recycle fibers on a mid-list removal and unmount the last row instead.
                    <VendorFeeRow
                      key={row.id}
                      index={index}
                      isReadOnly={isReadOnly}
                      onRemove={remove}
                    />
                  )
                })}
              </div>
            ) : null}
            {values.rows.length === 0 ? (
              <Typography variant="body2" css={tw`text-shades-500`}>
                No vendor fees are applied to this customer&apos;s payouts.
              </Typography>
            ) : null}
            {isReadOnly ? null : (
              <div css={tw`flex flex-row items-center justify-between gap-4`}>
                <Button
                  color="light"
                  size="small"
                  leftIcon={
                    <GSIcon iconName="gs_plus_medium" size={20} css={tw`text-primary-500`} />
                  }
                  onClick={() => {
                    return push(buildNextRow())
                  }}
                  data-testid="add-vendor-fee"
                >
                  Add vendor fee
                </Button>
                <Button
                  type="submit"
                  size="small"
                  color="dark"
                  className={tw`px-4`}
                  loading={isSaving}
                  disabled={!dirty}
                  data-testid="save-vendor-fees"
                >
                  Save vendor fees
                </Button>
              </div>
            )}
          </SectionCard>
        )
      }}
    </FieldArray>
  )
}

export const VendorFeesFormContent = memo(VendorFeesFormContentComponent)
